// src/services/aiChatService.js
import { getAuth } from 'firebase/auth';

// 1. PRODUCTION CONFIGURATION
// If REACT_APP_API_URL is set (e.g., in Vercel), use it.
// Otherwise, default to '' which uses relative paths (works with Proxy in dev).
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const getAuthToken = async () => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    if (user) {
      return await user.getIdToken();
    }
    return null;
  } catch (error) {
    console.error("Error getting auth token:", error);
    return null;
  }
};

export const getPersonas = async () => {
  const API_ENDPOINT = `${API_BASE_URL}/api/ai-lab/personas`;
  
  try {
    const token = await getAuthToken();
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

    const response = await fetch(API_ENDPOINT, { headers });
    if (!response.ok) {
      throw new Error(`Failed to fetch personas: ${response.statusText}`);
    }
    const data = await response.json();
    return data.personas || [];
  } catch (error) {
    console.error("Error fetching personas:", error);
    return [];
  }
};

export const sendMessageToBackend = async (
  userId,
  messageText,
  personaId
) => {
  const API_ENDPOINT = `${API_BASE_URL}/api/ai-lab/chat`;

  // DEBUG: Log the inputs
  console.log("sendMessageToBackend called with:", { userId, messageText, personaId });

  const payload = {
    user_id: userId,
    message_text: messageText,
    persona_id: personaId,
  };
  
  console.log("Sending JSON payload:", JSON.stringify(payload));

  try {
    const token = await getAuthToken();
    
    const response = await fetch(API_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: JSON.stringify(payload),
    });

    if (response.ok) {
      return await response.json();
    }

    const contentType = response.headers.get('content-type');
    let errorPayload;

    if (contentType && contentType.includes('application/json')) {
      const errorData = await response.json();
      errorPayload = errorData.error || `Server returned status ${response.status}`;
    } else {
      const errorText = await response.text();
      errorPayload = `Server returned status ${response.status}. Response: ${errorText.substring(0, 200)}...`;
    }

    throw new Error(errorPayload);

  } catch (error) {
    console.error("Error in sendMessageToBackend:", error);
    throw error;
  }
};

export const getChatHistory = async (
  userId,
  personaId,
  allPersonas
) => {
  console.log(`Fetching history for user: ${userId}, persona: ${personaId}`);
  
  const API_ENDPOINT = `${API_BASE_URL}/api/ai-lab/chat/history?persona_id=${personaId}`;

  try {
    const token = await getAuthToken();
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

    const response = await fetch(API_ENDPOINT, { headers });

    if (!response.ok) {
        // If 404, it usually means no history exists yet. 
        // We treat this as an empty list rather than an error.
        if (response.status === 404) return [];
        throw new Error(`Failed to fetch history: ${response.status}`);
    }

    const data = await response.json();
    let messages = data.messages || [];

    // Find the persona details to enrich the messages
    const persona = allPersonas ? allPersonas.find(p => p.id === personaId) : null;

    // --- THE FIX: Add Synthetic Greeting if History is Empty ---
    if (messages.length === 0 && persona) {
        messages = [{
            id: `greeting-${personaId}-${Date.now()}`,
            text: `Hello! I am ${persona.koruName}. What do you want to talk about now?`,
            sender: 'ai',
            personaId: personaId,
            timestamp: Date.now(),
            personaName: persona.koruName,
            personaAvatarUrl: persona.avatarUrl
        }];
    }

    // Map the backend messages to add frontend-specific fields like avatar
    return messages.map(msg => ({
        ...msg,
        // Ensure the avatar URL is attached if it's an AI message
        personaAvatarUrl: msg.sender === 'ai' && persona ? persona.avatarUrl : undefined,
        personaName: msg.sender === 'ai' && persona ? persona.koruName : msg.personaName
    }));

  } catch (error) {
    console.error("Error fetching chat history:", error);
    // Return empty array on error to prevent UI crash
    return [];
  }
};